import React from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerContentContainer = styled.div<{ height: number }>({
	background: token('color.background.warning'),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	padding: token('space.150'),
	color: token('color.text'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.height}px`,
	boxSizing: 'border-box',
	gap: token('space.050'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const BannerLink = styled.a({
	color: token('color.text'),
	textDecoration: 'underline',
	cursor: 'pointer',
	whiteSpace: 'nowrap',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:hover, :visited': {
		color: token('color.text'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		// accessibility focus indicator is cut off because of overflow: "hidden"
		// from the parent container
		outlineOffset: token('space.negative.025'),
		padding: token('space.025'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TextContainer = styled.div({
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	fontWeight: token('font.weight.medium'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionContainer = styled.span({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const i18n = defineMessages({
	warningIcon: {
		id: 'banners.icons.warning',
		defaultMessage: 'Warning icon',
		description:
			'The text is the accessible label for a warning icon displayed in the onboarding warning banner. Used by screen readers to convey that the icon represents a warning or alert.',
	},
	moreIcon: {
		id: 'banners.icons.more',
		defaultMessage: 'More icon',
		description:
			'The text is the accessible label for a more-options icon in the onboarding warning banner. Used by screen readers to convey that the icon opens an additional options or overflow menu.',
	},
	closeIcon: {
		id: 'banners.icons.close',
		defaultMessage: 'Close icon',
		description:
			'The text is the accessible label for a close icon in the onboarding warning banner. Used by screen readers to convey that the icon is used to dismiss or close the banner.',
	},
});

type WarningBannerProps = {
	bannerHeight: number;
	description: React.ReactNode;
	inlineDescriptionCTA?: React.ReactNode;
	rightSideComponent?: React.ReactNode;
};

export const OnboardingWarningBanner = ({
	bannerHeight,
	description,
	inlineDescriptionCTA,
	rightSideComponent,
}: WarningBannerProps) => {
	const { formatMessage } = useIntl();

	return (
		<BannerContentContainer height={bannerHeight}>
			<TextContainer>
				<StatusWarningIcon
					spacing="spacious"
					label={formatMessage(i18n.warningIcon)}
					color={token('color.icon.warning')}
				/>
				<DescriptionContainer>{description}</DescriptionContainer>
				{inlineDescriptionCTA}
			</TextContainer>
			{rightSideComponent}
		</BannerContentContainer>
	);
};
